/**
 * VoiceIntroStep — branded welcome card shown before voice onboarding starts. Shows the company
 * name and logo (if available), introduces SAM, and fades in a fixed description on mount.
 *
 * Shared by two flows with different CTAs:
 *  - Tenant-admin setup wizard (SetupWizardPage): passes `onProceed`, rendering a primary
 *    "Start voice setup" button (continues with voice) alongside "Set up manually instead".
 *  - Invited-user onboarding (UserOnboardingPage): omits `onProceed` — only "Set up manually
 *    instead" is shown, going straight to the profile form.
 */
import React, { useState, useEffect } from 'react'
import { VoiceFAB } from './VoiceFAB'
import { Button } from '@/components/UI/Button'
import type { VoiceStatus } from '@/redux/slices/onboardingVoiceSlice'

interface VoiceIntroStepProps {
  voiceStatus: VoiceStatus
  isVoiceActive: boolean
  /** Called when the user clicks "Set up manually instead" */
  onManual: () => void
  /** When provided, renders a primary "Start voice setup" button above "Set up manually
   *  instead" — used by the tenant-admin setup wizard to advance while keeping voice active. */
  onProceed?: () => void
  /** Company name shown above "Meet SAM" — e.g. "Eagle Peak Roofing LLC" */
  companyName?: string
  /** Brand logo URL; if omitted the mic FAB is the only presence */
  logoUrl?: string
}

const DESCRIPTION =
  'SAM is your voice-powered RoofVox assistant. It will help you complete your profile and show you how to get started — all through a short conversation.'

const VoiceIntroStep: React.FC<VoiceIntroStepProps> = ({
  voiceStatus,
  onManual,
  onProceed,
  companyName,
  logoUrl,
}) => {
  // Fade the description in once on mount — keeps animation but with a fixed message.
  const [fadeIn, setFadeIn] = useState(false)
  useEffect(() => {
    const t = setTimeout(() => setFadeIn(true), 80)
    return () => clearTimeout(t)
  }, [])

  // Voice auto-starts the moment this screen mounts (see SetupWizardPage/UserOnboardingPage), so
  // SAM's spoken intro begins immediately. Disable both buttons below for a fixed delay so a
  // user can't tap through and cut the greeting off mid-sentence. Fixed delay rather than
  // detecting when SAM actually finishes talking — simpler, doesn't adapt to a longer/shorter
  // greeting, but good enough here since the intro is a short, ~2-3 sentence script.
  const [canProceed, setCanProceed] = useState(false)
  useEffect(() => {
    const t = setTimeout(() => setCanProceed(true), 5000)
    return () => clearTimeout(t)
  }, [])

  return (
    <div className="card-surface relative overflow-hidden">
      {/* One static violet glow behind SAM, plus faint rings that turn slowly (still under reduced motion) */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(60% 55% at 50% 34%, rgb(var(--ui-accent) / 0.22), transparent 70%)' }}
      />
      <div aria-hidden="true" className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[440px] h-[440px] rounded-full border border-ui-border animate-[spin_60s_linear_infinite]" />
        <div className="absolute w-[300px] h-[300px] rounded-full border border-ui-border animate-[spin_45s_linear_infinite_reverse]" />
        <div className="absolute w-[160px] h-[160px] rounded-full border border-ui-border animate-[spin_30s_linear_infinite]" />
      </div>

      <div className="relative px-8 py-16 flex flex-col items-center text-center">
        {/* Brand logo — rendered above the mic FAB if available */}
        {logoUrl && (
          <div className="mb-5">
            <img
              src={logoUrl}
              alt={companyName ?? 'Company logo'}
              className="h-10 max-w-[140px] object-contain"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
            />
          </div>
        )}

        {/* Animated SAM presence */}
        <div className="mb-7">
          <VoiceFAB status={voiceStatus} size="lg" />
        </div>

        {/* Company welcome line — only shown when we have a name */}
        {companyName && (
          <p className="text-ui-text3 text-sm mb-1">Welcome to {companyName}!</p>
        )}

        <h2 className="text-h2 text-ui-text mb-4">Meet SAM</h2>

        {/* Fixed description that fades in once on mount */}
        <div className="mb-8 min-h-[3.5rem] flex items-start justify-center">
          <p
            className={`text-ui-text2 text-sm max-w-[300px] leading-relaxed transition-opacity duration-500 ${fadeIn ? 'opacity-100' : 'opacity-0'}`}
          >
            {DESCRIPTION}
          </p>
        </div>

        {/* Primary CTA — only for flows that pass onProceed (tenant-admin wizard); continues
            forward while keeping voice active. */}
        {onProceed && (
          <Button
            size="lg"
            onClick={onProceed}
            disabled={!canProceed}
            title={canProceed ? undefined : "Give SAM a moment to finish the intro…"}
            className="mb-2.5 w-full max-w-xs"
          >
            Start voice setup
          </Button>
        )}

        {/* Secondary/only CTA — goes directly to the profile form without voice */}
        <Button
          size={onProceed ? 'md' : 'lg'}
          variant={onProceed ? 'ghost' : 'primary'}
          onClick={onManual}
          disabled={!canProceed}
          title={canProceed ? undefined : "Give SAM a moment to finish the intro…"}
          className="w-full max-w-xs"
        >
          Set up manually instead
        </Button>
      </div>
    </div>
  )
}

export default VoiceIntroStep
